import React, { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { HandCoins, Plus, Trash2 } from 'lucide-react'
import api from '../api'
import { Card, PageHeader, Badge, EmptyState, Table, THead, td, Field, Button, Alert, Modal, ModalHeader, Input, Select } from '../components/ui'

function rp(n) { return 'Rp ' + Number(n || 0).toLocaleString('id-ID') }

const emptyForm = { teknisi_id: '', tanggal: new Date().toISOString().slice(0, 10), jumlah: '', keterangan: '' }

export default function Kasbon() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [data, setData] = useState([])
  const [saldo, setSaldo] = useState([])
  const [pegawaiList, setPegawaiList] = useState([])
  const [loading, setLoading] = useState(true)
  // Preset dari tombol "Kasbon" di halaman Pegawai (?teknisi_id=X) -- lihat Pegawai.jsx
  const [filterTeknisi, setFilterTeknisi] = useState(searchParams.get('teknisi_id') || '')

  const [modal, setModal] = useState(false)
  const [form, setForm] = useState(emptyForm)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => { load() }, [filterTeknisi])
  useEffect(() => { api.get('/pegawai').then(r => setPegawaiList(r.data.filter(p => p.aktif))) }, [])

  function ubahFilter(id) {
    setFilterTeknisi(id)
    setSearchParams(id ? { teknisi_id: id } : {})
  }

  async function load() {
    setLoading(true)
    try {
      const res = await api.get('/kasbon', { params: filterTeknisi ? { teknisi_id: filterTeknisi } : {} })
      setData(res.data.data)
      setSaldo(res.data.saldo)
    } finally { setLoading(false) }
  }

  function bukaTambah() {
    setForm({ ...emptyForm, teknisi_id: filterTeknisi || '' })
    setError('')
    setModal(true)
  }

  async function simpan() {
    if (!form.teknisi_id) return setError('Pegawai wajib dipilih')
    if (!form.tanggal) return setError('Tanggal wajib diisi')
    if (!form.jumlah || Number(form.jumlah) <= 0) return setError('Jumlah harus lebih dari 0')
    setSaving(true)
    try {
      await api.post('/kasbon', form)
      setModal(false)
      load()
    } catch (e) {
      setError(e.response?.data?.message || 'Gagal menyimpan')
    } finally { setSaving(false) }
  }

  async function hapus(row) {
    if (!window.confirm(`Hapus pengajuan kasbon ${rp(row.jumlah)} milik ${row.nama_teknisi}?`)) return
    try {
      await api.delete(`/kasbon/${row.id}`)
      load()
    } catch (e) {
      alert(e.response?.data?.message || 'Gagal menghapus')
    }
  }

  const ringkasan = filterTeknisi ? saldo.find(s => String(s.teknisi_id) === String(filterTeknisi)) : null

  return (
    <div>
      <PageHeader
        title="Kasbon"
        subtitle="Riwayat pengajuan & cicilan kasbon pegawai -- cicilan otomatis tercatat saat admin membuat slip Payroll"
        actions={<Button icon={Plus} onClick={bukaTambah}>Tambah Kasbon</Button>}
      />

      {ringkasan ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12, marginBottom: 20 }}>
          <Card style={{ padding: '14px 16px' }}>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>{ringkasan.nama} — Total Diajukan</div>
            <div style={{ fontSize: 17, fontWeight: 700 }}>{rp(ringkasan.total_diajukan)}</div>
          </Card>
          <Card style={{ padding: '14px 16px' }}>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>Total Dibayar</div>
            <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--success)' }}>{rp(ringkasan.total_dibayar)}</div>
          </Card>
          <Card style={{ padding: '14px 16px' }}>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>Sisa</div>
            <div style={{ fontSize: 17, fontWeight: 700, color: ringkasan.sisa > 0 ? 'var(--danger)' : 'var(--success)' }}>{rp(ringkasan.sisa)}</div>
          </Card>
        </div>
      ) : saldo.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12, marginBottom: 20 }}>
          {saldo.map(s => (
            <Card key={s.teknisi_id} style={{ padding: '14px 16px' }}>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>{s.nama}</div>
              <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--danger)' }}>{rp(s.sisa)}</div>
              <div style={{ fontSize: 11, color: 'var(--text-faint)' }}>saldo outstanding</div>
            </Card>
          ))}
        </div>
      )}

      <div style={{ marginBottom: 14, maxWidth: 260 }}>
        <Select value={filterTeknisi} onChange={e => ubahFilter(e.target.value)}>
          <option value="">Semua pegawai</option>
          {pegawaiList.map(p => <option key={p.id} value={p.id}>{p.nama}</option>)}
        </Select>
      </div>

      <Table>
        <THead cols={['Tanggal', 'Pegawai', 'Jenis', 'Jumlah', 'Keterangan', '']} />
        <tbody>
          {loading ? (
            <tr><td colSpan={6}><EmptyState icon={HandCoins} title="Memuat..." /></td></tr>
          ) : data.length === 0 ? (
            <tr><td colSpan={6}><EmptyState icon={HandCoins} title="Belum ada riwayat kasbon" /></td></tr>
          ) : data.map(row => (
            <tr key={`${row.type}-${row.id}`}>
              <td style={td}>{row.tanggal}</td>
              <td style={{ ...td, fontWeight: 600 }}>{row.nama_teknisi}</td>
              <td style={td}>
                {row.type === 'pengajuan'
                  ? <Badge tone="warning">Diajukan</Badge>
                  : <Badge tone="success">Dicicil — Periode {row.periode}</Badge>}
              </td>
              <td style={td}>{row.type === 'pengajuan' ? rp(row.jumlah) : <span style={{ color: 'var(--success)' }}>-{rp(row.jumlah)}</span>}</td>
              <td style={{ ...td, color: 'var(--text-muted)' }}>{row.keterangan || '-'}</td>
              <td style={td}>
                {row.type === 'pengajuan' && (
                  <button onClick={() => hapus(row)} title="Hapus" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)' }}>
                    <Trash2 size={15} />
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {modal && (
        <Modal onClose={() => setModal(false)} width={420}>
          <ModalHeader title="Tambah Kasbon" onClose={() => setModal(false)} />
          {error && <Alert type="error">{error}</Alert>}
          <Field label="Pegawai *" style={{ marginBottom: 14 }}>
            <Select value={form.teknisi_id} onChange={e => setForm({ ...form, teknisi_id: e.target.value })}>
              <option value="">Pilih pegawai</option>
              {pegawaiList.map(p => <option key={p.id} value={p.id}>{p.nama}</option>)}
            </Select>
          </Field>
          <Field label="Tanggal *" style={{ marginBottom: 14 }}>
            <Input type="date" value={form.tanggal} onChange={e => setForm({ ...form, tanggal: e.target.value })} />
          </Field>
          <Field label="Jumlah (Rp) *" style={{ marginBottom: 14 }}>
            <Input type="number" value={form.jumlah} onChange={e => setForm({ ...form, jumlah: e.target.value })} placeholder="0" />
          </Field>
          <Field label="Keterangan" style={{ marginBottom: 8 }}>
            <Input value={form.keterangan} onChange={e => setForm({ ...form, keterangan: e.target.value })} placeholder="mis. keperluan pribadi" />
          </Field>
          <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
            <Button variant="neutral" onClick={() => setModal(false)} style={{ flex: 1, justifyContent: 'center' }}>Batal</Button>
            <Button loading={saving} onClick={simpan} style={{ flex: 1, justifyContent: 'center' }}>Simpan</Button>
          </div>
        </Modal>
      )}
    </div>
  )
}
